import type { SearchParamCodec } from '@repo/ui/lib/hooks/use-search-params-form';

/** Il nome dell'unico campo di `useSearchParamsForm` che porta il pannello. */
export const PANEL_FILTERS_FIELD = 'filters';

export interface PanelSearchParamOptions<TValues> {
  /**
   * Tutti i parametri dell'URL che il pannello legge e scrive, compresi i
   * deep link storici che la lettura accetta e la scrittura azzera: solo
   * questi arrivano a `toValues`, e «Azzera tutto» toglie proprio questi.
   */
  keys: readonly string[];
  /** URL → valori del pannello (tollerante: un valore illeggibile è vuoto). */
  toValues: (search: Record<string, unknown>) => TValues;
  /** Valori del pannello → parametri dell'URL (`null`/`undefined` = assente). */
  toUrlParams: (values: TValues) => Record<string, unknown>;
  /** I valori vuoti del pannello: la scrittura parte da qui se manca un valore. */
  emptyValues: TValues;
}

/**
 * Il pannello Filtri a bozza (tavola 8a) come UN campo di
 * `useSearchParamsForm`: i valori del pannello sono un oggetto (periodi,
 * insiemi, importi) che nell'URL diventa più parametri con i nomi dell'API.
 * Le due funzioni pure della lista (`toValues`/`toUrlParams`) sono già
 * l'una l'inversa dell'altra: il codec le usa intere, così nessun parametro
 * si scrive guardando un solo campo e la conversione converge.
 *
 * `keys` deve coprire ogni parametro che `toUrlParams` scrive: un parametro
 * scritto ma non dichiarato non tornerebbe indietro dall'URL (il form legge
 * solo i parametri dei suoi campi). Il controllo è alla costruzione.
 */
export function panelSearchParam<TValues>({
  keys,
  toValues,
  toUrlParams,
  emptyValues,
}: PanelSearchParamOptions<TValues>): SearchParamCodec<string, TValues> {
  const undeclared = Object.keys(toUrlParams(emptyValues)).filter(
    (key) => !keys.includes(key)
  );
  if (undeclared.length > 0) {
    throw new Error(
      `panelSearchParam: parametri scritti ma non dichiarati in keys: ${undeclared.join(', ')}`
    );
  }

  return {
    keys,
    decode: (search) => toValues(search),
    encode: (value) =>
      Object.fromEntries(
        Object.entries(toUrlParams(value ?? emptyValues)).filter(([key]) =>
          keys.includes(key)
        )
      ),
  };
}

/** I campi di `useSearchParamsForm` di una lista col pannello a bozza. */
export function panelSearchFields<TValues>(
  codec: SearchParamCodec<string, TValues>
): { readonly [PANEL_FILTERS_FIELD]: SearchParamCodec<string, TValues> } {
  return { [PANEL_FILTERS_FIELD]: codec };
}
